import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { PersonForm, AddressForm, DocumentForm } from '@/components/forms'
import { clientSchema } from '@/lib/schemas'
import { type Client } from '@/services/clients'
import { useUpdateClient } from '@/hooks/useUpdateClient'
import { Loader2 } from 'lucide-react'

/**
 * A l'edició, document i cognoms es validen com a text lliure, com fa el backend: els clients
 * reals porten `DNI`/`CIF` antics (Cuentica, alta manual) o cap document, i les empreses no
 * tenen cognoms. Amb l'enum i el `min(1)` del schema d'alta, el modal no desava la majoria.
 */
const editClientSchema = clientSchema.partial().extend({
  cognoms: z.string().optional(),
  tipus_document: z.string().optional(),
  num_document: z.string().optional(),
  // Senyals de la card (`plans/calendar-card-signals.md` §6 F2.6): client o empresa de confiança
  payOnArrival: z.boolean().optional(),
})

interface EditClientModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  client: Client
}

export function EditClientModal({ open, onOpenChange, onSuccess, client }: EditClientModalProps) {
  const updateClientMutation = useUpdateClient()

  const form = useForm({
    resolver: zodResolver(editClientSchema),
    defaultValues: {
      nom: '',
      cognoms: '',
      email: '',
      telefon: '',
      mobil: '',
      tipus_document: '',
      num_document: '',
      adreca: '',
      ciutat: '',
      provincia: '',
      codi_postal: '',
      pais: 'ES',
      empresa: '',
      nif_empresa: '',
      idioma_preferit: 'ca',
      notes: '',
      consentiment_marketing: false,
      payOnArrival: false,
    },
  })

  // Cargar datos del cliente cuando se abre el modal
  useEffect(() => {
    if (open && client) {
      form.reset({
        nom: client.nom || '',
        cognoms: client.cognoms || '',
        email: client.email || '',
        telefon: client.telefon || '',
        mobil: client.mobil || '',
        tipus_document: client.tipus_document || '',
        num_document: client.num_document || '',
        adreca: client.adreca || '',
        ciutat: client.ciutat || '',
        provincia: client.provincia || '',
        codi_postal: client.codi_postal || '',
        pais: client.pais || 'ES',
        empresa: client.empresa || '',
        nif_empresa: client.nif_empresa || '',
        idioma_preferit: client.idioma_preferit || 'ca',
        notes: client.notes || '',
        consentiment_marketing: client.consentiment_marketing || false,
        payOnArrival: client.payOnArrival ?? false,
      })
    }
  }, [open, client, form])

  const onSubmit = async (data: Record<string, unknown>) => {
    // Un camp buit que ja era buit no s'envia: no converteix un null en '' ni topa amb els
    // validadors del backend (`email`, `cognoms`) en clients que no en tenen. Esborrar un camp
    // que tenia valor (també les notes) sí que s'envia.
    const payload = Object.fromEntries(
      Object.entries(data).filter(([key, value]) => !(value === '' && !client[key as keyof Client])),
    ) as Partial<Client>
    // «Paga en arribar» només s'envia si ha canviat: així un client que no el toca desa igual.
    if (payload.payOnArrival === (client.payOnArrival ?? false)) delete payload.payOnArrival
    // Les notes van al mateix PATCH: el backend les desa a Person.notes o, si el client no té
    // persona (empreses de Cuentica), a Client.notes (F6)
    updateClientMutation.mutate(
      { id: client.id, data: payload },
      {
        onSuccess: () => {
          onSuccess()
          onOpenChange(false)
        }
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-green-700">
            Editar Client
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Dades Personals */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Dades Personals</h3>
              <PersonForm
                form={form}
                fields={['nom', 'cognoms', 'email', 'telefon', 'mobil']}
                required={['nom', 'cognoms', 'email']}
              />
            </div>

            <Separator />

            {/* Document d'Identitat */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Document d'Identitat</h3>
              <DocumentForm form={form} />
            </div>

            <Separator />

            {/* Adreça */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Adreça</h3>
              <AddressForm form={form} />
            </div>

            <Separator />

            {/* Empresa */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Informació Empresa</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="empresa"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Empresa</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Nom de l'empresa" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nif_empresa"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>NIF Empresa</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="A12345678" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Idioma Preferit */}
            <FormField
              control={form.control}
              name="idioma_preferit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Idioma Preferit</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona idioma" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="ca">Català</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="de">Deutsch</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            {/* Notes */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes Internes</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} placeholder="Notes internes..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Consentiment Marketing */}
            <FormField
              control={form.control}
              name="consentiment_marketing"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Consentiment per enviar comunicacions comercials</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <Separator />

            {/* Client de confiança: la franja de pagament queda en taronja fins al dia de sortida */}
            <FormField
              control={form.control}
              name="payOnArrival"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between gap-3 space-y-0">
                  <div className="space-y-1">
                    <FormLabel>Paga en arribar (client de confiança)</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Les seves reserves no es posen en vermell a 7 dies d'arribar: es cobra en marxar.
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                      aria-label="Paga en arribar (client de confiança)"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Botons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateClientMutation.isPending}
              >
                Cancel·lar
              </Button>
              <Button
                type="submit"
                className="bg-green-600 hover:bg-green-700"
                disabled={updateClientMutation.isPending}
              >
                {updateClientMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Actualitzant...
                  </>
                ) : (
                  'Actualitzar Client'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
